import { defineTable } from "convex/server";
import { v } from "convex/values";
import {
  internalControl,
  sharedValidators,
  validatorDisplaySortItem,
} from "../../lib/shared/validators";
import { paymentMethods } from "./payments";

export const invoiceStatus = v.union(
  v.literal("pending"),
  v.literal("awaiting_payment"),
  v.literal("completed"),
  v.literal("cancelled"),
);

const tax = v.object({
  taxAmount: v.number(),
  taxRate: v.number(),
  itemsQuantity: v.number(),
  taxableAmount: v.optional(v.number()),
});

export const validatorInvoice = v.object({
  clientId: v.optional(v.id("clients")),
  sellerId: v.optional(v.id("sellers")),
  saleId: v.optional(v.id("sales")),
  workOrderId: v.optional(v.id("work_orders")),
  paymentId: v.optional(v.id("invoices_payments")),
  code: v.string(),
  currency: sharedValidators.currencyValue,
  correlative: v.optional(v.string()),
  status: invoiceStatus,
  subtotal: v.number(),
  taxAmount: v.number(),
  totalAmount: v.number(),
  dueDate: v.optional(v.number()),
  notes: v.optional(v.string()),
  internalControl,
  taxes: v.array(tax),
  paymentTerms: v.optional(v.string()),
  paymentMethod: v.optional(paymentMethods),
  issueDate: v.number(),
  exemptAmount: v.optional(v.number()),
  discountAmount: v.optional(v.number()),
  // Descuento general aplicado sobre el subtotal neto (tras descuentos por item).
  globalDiscountType: v.optional(
    v.union(v.literal("percentage"), v.literal("fixed")),
  ),
  globalDiscountValue: v.optional(v.number()),
  // Permite al usuario decidir si usar crédito aunque el cliente lo tenga habilitado.
  useCredit: v.optional(v.boolean()),
  cancellationRequestId: v.optional(v.id("invoices_cancelation_requests")),
  // SAR/SARIN Honduras: identificación del emisor (G-001/G-031).
  issuerRTN: v.optional(v.string()),
  issuerNRC: v.optional(v.string()),
  // SAR/SARIN Honduras: identificación del receptor (G-002/G-032).
  receiverRTN: v.optional(v.string()),
  receiverName: v.optional(v.string()),
  // true cuando es "Consumidor Final" (sin clientId).
  isConsumerFinal: v.optional(v.boolean()),
  // CAI con el que se emitió la factura; se referencia en notas de crédito/débito (SAR Art. 26.3.a).
  printingAuthorizationCode: v.optional(v.string()),
  // SAR/SARIN Honduras: número de orden de exoneración (G-034).
  exonerationNumber: v.optional(v.string()),
  // SAR/SARIN Honduras: retención de ISR (G-030).
  isrRetentionRate: v.optional(v.number()),
  isrRetentionAmount: v.optional(v.number()),
  // Emisión offline: `offlineId` uuid del cliente para dedupe idempotente; `issuedOffline` indica correlativo asignado sin red.
  offlineId: v.optional(v.string()),
  issuedOffline: v.optional(v.boolean()),
  // Sort visual persistido en la BD para que el PDF respete el orden que
  // dejó el usuario en la tabla de ítems. Mismo algoritmo que `quotations`.
  displaySort: v.optional(v.array(validatorDisplaySortItem)),
});
export const invoices = defineTable(validatorInvoice)
  .index("by_company_and_code", ["internalControl.companyId", "code"])
  .index("by_company_and_status", ["internalControl.companyId", "status"])
  .index("by_company_and_correlative", [
    "internalControl.companyId",
    "correlative",
  ])
  .index("by_sale_id", ["saleId"])
  .index("by_work_order_id", ["workOrderId"])
  .index("by_company_id", ["internalControl.companyId"])
  .index("by_client_id", ["clientId"])
  .index("by_status", ["status"])
  .index("by_created_at", ["internalControl.createdAt"])
  .index("by_company_and_created_at", [
    "internalControl.companyId",
    "internalControl.createdAt",
  ])
  .index("by_company_and_branch", [
    "internalControl.companyId",
    "internalControl.branchId",
  ])
  // Dashboard: rango de fechas dentro de una sucursal sin traer toda la sucursal.
  .index("by_company_and_branch_and_created_at", [
    "internalControl.companyId",
    "internalControl.branchId",
    "internalControl.createdAt",
  ])
  // Dedupe de facturas emitidas offline al sincronizar (uuid del cliente).
  .index("by_offline_id", ["offlineId"]);
